import axios from 'axios';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { DateTimeFormat, formatMoment } from '@/shared/converters/dateConverters';
import { formatDossierContactTargets } from '@/shared/converters/dossierContactTargetConverters';
import DossierContactTarget from '@/shared/models/DossierContactTarget';
import { OutletId } from '@/shared/models/Outlet';
import { present } from '@/shared/utils/typescript';
import SurveySearchByOptions from '../constants/SurveySearchByOptions';
import { parseSurvey, parseSurveys } from '../converters/surveyConverters';
import Survey, { SurveyId } from '../models/Survey';
import SurveyFilters from '../models/SurveyFilters';

export interface SurveyFeedbackData {
  comment: string;
  finalize: boolean;
}

export interface SurveySearchResult {
  answeredSurveys: Array<Survey>;
  sentSurveysCount: Map<OutletId, number>;
}

export default class SurveyClient {
  static async searchForFollowUp(filters: SurveyFilters): Promise<SurveySearchResult> {
    const params = {
      dateFrom: formatMoment(filters.followedUpFrom, DateTimeFormat.ROUTE_DATE_TIME),
      dateTill: formatMoment(filters.followedUpTill, DateTimeFormat.ROUTE_DATE_TIME),
      moboOutletIds: [filters.commonFilters.activeOutletIds],
      includeExcludedForFollowUp: filters.includeFollowedUp,
      onlyNegative: filters.onlyNegative,
    };
    const response = await axios.get('/api/mobopro/v1/cssfollowup', { params });
    return {
      answeredSurveys: parseSurveys(response.data.dossiers),
      sentSurveysCount: new Map(response.data.moboOutlets.map((o: any) => [o.moboOutletId, o.counterSent ?? 0])),
    };
  }

  static async loadByDossier(dossierId: DossierId): Promise<Array<Survey>> {
    const response = await axios.get('/api/mobopro/v1/css', {
      params: {
        searchBy: SurveySearchByOptions.DOSSIER,
        searchFor: dossierId,
        mostRecent: false,
      },
    });
    return parseSurveys(response.data.dossiers);
  }

  static async finalize(id: SurveyId, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/cssfollowup/${dossierId}`, {
      id,
      followUp: null,
      isExcludedForFollowUp: true,
    });
  }

  static async create(dossierId: DossierId, targets: Array<DossierContactTarget>): Promise<Survey> {
    const response = await axios.post('/api/mobopro/v1/dossiers/css', {
      dossierId,
      targets: formatDossierContactTargets(targets),
    });
    return present(parseSurvey(response.data));
  }

  static async submitFeedback(survey: Survey, data: SurveyFeedbackData): Promise<Survey> {
    const response = await axios.patch(`/api/mobopro/v1/cssfollowup/${survey.dossier.id}`, {
      id: survey.id,
      followUp: data.comment,
      isExcludedForFollowUp: data.finalize,
    });
    return present(parseSurvey(response.data));
  }
}
